'use client';

import { Product, GA4EventParams } from '@akinon/next/types';
import {
  BreadcrumbResultType,
  GTMEvent,
  GTMPromotionData,
  GTMUserType
} from '@theme/types';
import CryptoJS from 'crypto-js';
import { findProductIndexInStorage } from './find-product-index';
import { buildClientRequestUrl } from '@akinon/next/utils';
import { getEmarsysProduct } from './get-emarsys';

const DEFAULT_CURRENCY = 'TRY';
const DEFAULT_AFFILIATION = 'FashFed';

export type SearchType = 'image_search' | 'text_search';

const SEARCH_TYPE_STORAGE_KEY = 'search_type';

export const getSearchType = (): SearchType => {
  try {
    return localStorage.getItem(SEARCH_TYPE_STORAGE_KEY) === 'image_search'
      ? 'image_search'
      : 'text_search';
  } catch {
    return 'text_search';
  }
};

export const setSearchType = (value: SearchType) => {
  try {
    localStorage.setItem(SEARCH_TYPE_STORAGE_KEY, value);
  } catch (error) {
    console.error('Error setting search type in localStorage:', error);
  }
};

export const resetSearchType = () => setSearchType('text_search');

type ImageSearchAction = 'search_start' | 'image_search_result_loaded';
type ImageSearchPageType = 'search' | 'product_detail' | 'favorites';
type ImageSearchTriggerMethod = 'gallery' | 'camera';

let imageSearchResultPending = false;

export const consumeImageSearchPending = (): boolean => {
  if (!imageSearchResultPending) return false;
  imageSearchResultPending = false;
  return true;
};

export const pushImageSearchInteraction = ({
  action,
  pageType,
  triggerMethod = 'gallery',
  userId
}: {
  action: ImageSearchAction;
  pageType: ImageSearchPageType;
  triggerMethod?: ImageSearchTriggerMethod;
  userId?: string;
}) => {
  if (action === 'search_start') {
    imageSearchResultPending = true;
  }

  window?.dataLayer?.push({
    event: 'image_search_interaction',
    action,
    page_type: pageType,
    trigger_method: triggerMethod,
    user_id: userId || ''
  });
};

const getIsGift = (product: Product) => {
  const gender = typeof window !== 'undefined' ? window.GLOBALS.gender : '';
  return (
    (product?.attributes?.integration_cinsiyet === 'Kadın' && gender === 'male') ||
    (product?.attributes?.integration_cinsiyet === 'Erkek' && gender === 'female')
  )
    ? 'Yes'
    : 'No';
};

export const enhancedProductData = ({
  product,
  index
}: {
  product: Product;
  index: number;
}) => {
  const formatPrice = (price: number) =>
    price
      .toLocaleString('en-US', { minimumFractionDigits: 2 })
      .replace(',', '.');

  const calculateDiscountPercentage = (retailPrice: number, price: number) =>
    ((1 - price / retailPrice) * 100).toFixed(2);

  return {
    id: product?.base_code,
    name: product?.name,
    price: formatPrice(+product.price),
    currency: product?.currency_type,
    brand: product?.attributes_kwargs?.integration_marka?.label,
    category: `${product?.attributes?.integration_cinsiyet || ''} > ${
      product?.attributes_kwargs?.integration_urun_ana_grup?.label || ''
    } > ${product?.attributes_kwargs?.integration_alt_grup?.label || ''}`,
    variant:
      product?.attributes?.erp_renk_tonu ||
      product?.attributes_kwargs?.integration_renk_tonu?.label ||
      '',
    position: index,
    ...(product?.attributes?.badge === 'SÜRDÜRÜLEBİLİR' && {
      sustainability: 'yes'
    }),
    dimension1: product?.sku,
    dimension2: product?.in_stock ? 'Yes' : 'No',
    dimension3: product?.retail_price !== product.price ? 'Yes' : 'No',
    dimension4:
      product?.retail_price !== product?.price
        ? calculateDiscountPercentage(+product?.retail_price, +product?.price)
        : '0',
    dimension5:
      product?.attributes?.mp_color ||
      product?.attributes?.integration_renk_tonu ||
      '-',
    dimension6: `${product?.base_code}${
      product?.attributes?.integration_renk
        ? `.${product?.attributes?.integration_renk}`
        : ''
    }`
  };
};

const buildEmarsysFromProduct = (product: Product) => {
  const cinsiyet = product?.attributes?.integration_cinsiyet || '';
  const anagrup =
    product?.attributes_kwargs?.integration_urun_ana_grup?.label || '';
  const altgrup =
    product?.attributes_kwargs?.integration_alt_grup?.label || '';

  const parts = [cinsiyet, anagrup, altgrup].filter(Boolean);
  return parts.length > 0 ? parts.join(' > ') : '';
};

export const prepareProductData = ({
  product,
  index,
  affiliate,
  quantity = 1,
  emarsysCategory,
  forceInStock
}: {
  product: Product;
  index: number;
  affiliate?: string;
  quantity?: number;
  emarsysCategory?: string;
  forceInStock?: boolean;
}) => {
  const productColor =
    product?.attributes?.integration_renk ||
    product?.attributes?.integration_renk_tonu_kod ||
    product?.attributes_kwargs?.integration_renk_tonu_kod?.value ||
    '-';

  const anagrup = product?.attributes_kwargs?.integration_urun_ana_grup;
  const altgrup = product?.attributes_kwargs?.integration_alt_grup;
  const brand = product?.attributes_kwargs?.integration_marka?.label;
  const discount = +product.retail_price - +product.price;
  const affiliation =
    affiliate ||
    (product as { data_source: { name: string } })?.data_source?.name ||
    DEFAULT_AFFILIATION;
   const cd_emarsys =
    emarsysCategory ||
    getEmarsysProduct(product?.pk) ||
    buildEmarsysFromProduct(product);

  return {
    affiliation,
    coupon: '',
    discount,
    discounted_price:
      parseFloat(product?.price) || parseFloat(product?.retail_price),
    gtin: product?.attributes?.integration_yurt_disi_barkod || '',
    in_stock: forceInStock ? 'Yes' : product?.in_stock ? 'Yes' : 'No',
    index,
    item_brand: brand || '',
    item_category: product?.attributes?.integration_cinsiyet || '',
    item_category2: anagrup?.label || '',
    item_category3: altgrup?.label || '',
    item_id: `${product?.base_code}.${productColor}`,
    item_list_id: anagrup?.value || altgrup?.value || '',
    item_list_name: `${brand} ${anagrup?.label || altgrup?.label || ''}`,
    item_model: product?.base_code || '',
    item_name: product?.name,
    item_sku: product?.sku || '',
    item_variant: product?.attributes?.filterable_size || product?.attributes?.integration_beden || '',
    price: parseFloat(product?.retail_price),
    cd_emarsys,
    quantity
  };
};

//In GA4 this is automatically collected. No need to push it manually for GA4.
export const pushPageView = (url: string) => {
  pushEventGA4('page_view', {
    page_location: url
  });
};

export const pushAddToCart = async (
  product,
  quantity = 1,
  cd_emarsys?: string,
  cartIndex?: number
) => {
  const qty = Number(quantity) || 1;

  const data = await fetch(
    buildClientRequestUrl('/baskets/basket', {
      contentType: 'application/json',
      responseType: 'json'
    })
  )
    .then((response) => response.json())
    .catch((error) => {
      console.error(error);
    });

  const { index: plpIndex, affiliate } = findProductIndexInStorage({
    product,
    productBaseCode: product?.base_code
  });

  const index = typeof cartIndex === 'number' && cartIndex > 0 ? cartIndex : plpIndex;

  const ecommerceData = prepareProductData({
    product,
    index,
    affiliate,
    quantity: qty,
    emarsysCategory: cd_emarsys
  });

  window?.dataLayer.push({
    ecommerce: {
      currency: product?.currency_type?.toUpperCase() || DEFAULT_CURRENCY,
      value:
        parseFloat(product?.price) * qty ||
        parseFloat(product?.retail_price) * qty,
      items: [
        {
          ...ecommerceData,
          is_gift: getIsGift(product)
        }
      ]
    },
    event: 'add_to_cart',
    search_type: getSearchType()
  });

  pushEvent({
    Action: 'Add',
    Label: 'Add to Cart',
    event: 'eeCart',
    noninteraction: false,
    ecommerce: {
      add: {
        products: [
          {
            ...enhancedProductData({
              product,
              index
            }),
            cart_id: data?.basket?.pk || ''
          }
        ]
      }
    }
  });
};

export const pushRemoveFromCart = async (
  product,
  quantity = 1,
  cartIndex?: number
) => {
  const qty = Number(quantity) || 1;

  const data = await fetch(
    buildClientRequestUrl('/baskets/basket', {
      contentType: 'application/json',
      responseType: 'json'
    })
  )
    .then((response) => response.json())
    .catch((error) => {
      console.error(error);
    });

  const { index: plpIndex, affiliate } = findProductIndexInStorage({
    product,
    productBaseCode: product?.base_code
  });

  const index = typeof cartIndex === 'number' && cartIndex > 0 ? cartIndex : plpIndex;

  const ecommerceData = prepareProductData({
    product,
    index,
    affiliate,
    quantity: qty
  });

  window?.dataLayer.push({
    ecommerce: {
      currency: product?.currency_type?.toUpperCase() || DEFAULT_CURRENCY,
      value:
        parseFloat(product?.price) * qty ||
        parseFloat(product?.retail_price) * qty,
      items: [{
        ...ecommerceData,
        is_gift: getIsGift(product)
      }]
    },
    event: 'remove_from_cart'
  });

  pushEvent({
    Action: 'Remove',
    Label: 'Remove from Cart',
    event: 'eeCart',
    noninteraction: false,
    ecommerce: {
      remove: {
        products: [
          {
            ...enhancedProductData({
              product,
              index
            }),
            cart_id: data?.basket?.pk || ''
          }
        ]
      }
    }
  });
};

export const pushCartView = (products: Product[], totalPrice: number) => {
  const productCurrency = products?.[0]?.currency_type;

  const items = products?.map((product, idx) => {
    const { affiliate } = findProductIndexInStorage({
      product,
      productBaseCode: product?.base_code
    });

    const ecommerceData = prepareProductData({
      product,
      index: idx + 1,
      affiliate,
      quantity: +product?.quantity || 1
    });

    return {
      ...ecommerceData,
      is_gift: getIsGift(product)
    };
  });

  window?.dataLayer.push({
    ecommerce: {
      currency: productCurrency?.toUpperCase() || DEFAULT_CURRENCY,
      value: totalPrice,
      items
    },
    event: 'view_cart',
    search_type: getSearchType()
  });

  pushEvent({
    Action: 'Checkout',
    Label: 'Sepet',
    event: 'eeCheckout',
    noninteraction: true,
    ecommerce: {
      actionField: { step: 1, action: 'checkout' },
      cartView: {
        products: products?.map((product) => {
          const { index } = findProductIndexInStorage({
            product,
            productBaseCode: product?.base_code
          });

          return {
            ...enhancedProductData({
              product,
              index
            }),
            gift_wrap: product?.gift_wrap,
            is_gift: getIsGift(product),
            quantity: +product?.quantity || 1
          };
        })
      }
    }
  });
};

export const pushBeginCheckout = (products: Product[], totalPrice: number) => {
  const productCurrency = products?.[0]?.currency_type;

  const items = products?.map((product, idx) => {
    const { affiliate } = findProductIndexInStorage({
      product,
      productBaseCode: product?.base_code
    });

    const ecommerceData = prepareProductData({
      product,
      index: idx + 1,
      affiliate,
      quantity: +product?.quantity || 1
    });

    return {
      ...ecommerceData,
      is_gift: getIsGift(product)
    };
  });

  pushEventGA4('begin_checkout', {
    search_type: getSearchType(),
    ecommerce: {
      currency: productCurrency?.toUpperCase() || DEFAULT_CURRENCY,
      value: totalPrice,
      items
    }
  });
};

export const pushAddShippingInfo = (
  products: Product[],
  shipping_tier?: string,
  totalPrice?: number
) => {
  const productCurrency = products?.[0]?.currency_type;

  const items = products?.map((product, idx) => {
    const { affiliate } = findProductIndexInStorage({
      product,
      productBaseCode: product?.base_code
    });

    const ecommerceData = prepareProductData({
      product,
      index: idx + 1,
      affiliate,
      quantity: +product?.quantity || 1
    });

    return {
      ...ecommerceData,
      is_gift: getIsGift(product)
    };
  });

  pushEventGA4('add_shipping_info', {
    search_type: getSearchType(),
    ecommerce: {
      currency: productCurrency?.toUpperCase() || DEFAULT_CURRENCY,
      value: totalPrice,
      shipping_tier: shipping_tier || '',
      items
    }
  });
};

export const pushAddPaymentInfo = (
  products: Product[],
  payment_type: string,
  totalPrice: number
) => {
  const productCurrency = products?.[0]?.currency_type;

  const items = products?.map((product, idx) => {
    const { affiliate } = findProductIndexInStorage({
      product,
      productBaseCode: product?.base_code
    });

    const ecommerceData = prepareProductData({
      product,
      index: idx + 1,
      affiliate,
      quantity: +product?.quantity || 1
    });

    return {
      ...ecommerceData,
      is_gift: getIsGift(product)
    };
  });

  pushEventGA4('add_payment_info', {
    search_type: getSearchType(),
    ecommerce: {
      currency: productCurrency?.toUpperCase() || DEFAULT_CURRENCY,
      value: totalPrice,
      payment_type: payment_type || '',
      items
    }
  });
};

export const pushSelectMainCategory = (categoryName: string) => {
  window?.dataLayer?.push({ ecommerce: null });
  window?.dataLayer?.push({
    event: 'select_main_category',
    category_name: categoryName || ''
  });
};

export const pushPaymentMethodTracking =(eventCategory: string) => {
  window.dataLayer.push({
    event: 'payment_method_tracking',
    event_category: eventCategory || ''
  });
};

export const pushProductListProductViewed = (
  products: Product[],
  cd_emarsys?: string
) => {
  const chunkSize = 4;

  products?.forEach((_product, index) => {
    const chunkIndex = Math.floor(index / chunkSize);

    if (index % chunkSize === 0) {
      const chunkProducts = products?.slice(
        chunkIndex * chunkSize,
        (chunkIndex + 1) * chunkSize
      );

      const ecommerceData = chunkProducts?.map((chunkProduct) => {
        const item = prepareProductData({
          product: chunkProduct,
          index: chunkProduct?.index + 1,
          emarsysCategory: cd_emarsys
        });

        return {
          ...item,
          is_gift: getIsGift(chunkProduct)
        };
      });

      window?.dataLayer.push({
        ecommerce: {
          currency:
            products?.[0]?.currency_type?.toUpperCase() || DEFAULT_CURRENCY,
          items: ecommerceData
        },
        event: 'view_item_list'
      });

      pushEvent({
        Action: 'Browse',
        Label: 'Product Impressions',
        event: 'eeEvent',
        noninteraction: true,
        ecommerce: {
          impressions: {
            0: chunkProducts?.map((product) => ({
              ...enhancedProductData({
                product,
                index: product?.index + 1
              }),
              list:
                typeof window !== 'undefined'
                  ? window.location.pathname.replace(/\//g, '')
                  : ''
            }))
          }
        }
      });
    }
  });
};

export const pushProductClicked = (
  product: Product,
  index: number,
  cd_emarsys?: string
) => {
  const ecommerceData = prepareProductData({
    product,
    index,
    emarsysCategory: cd_emarsys
  });

  window?.dataLayer.push({
    ecommerce: {
      currency: product?.currency_type?.toUpperCase() || DEFAULT_CURRENCY,
      items: [
        {
          ...ecommerceData,
          is_gift: getIsGift(product)
        }
      ]
    },
    event: 'select_item',
    search_type: getSearchType()
  });

  pushEvent({
    Action: 'Click',
    Label: 'Product Click',
    event: 'eeEvent',
    noninteraction: false,
    ecommerce: {
      click: {
        actionField: {
          list:
            typeof window !== 'undefined'
              ? window.location.pathname.replace(/\//g, '')
              : ''
        },
        products: [
          {
            ...enhancedProductData({
              product,
              index
            }),
            list:
              typeof window !== 'undefined'
                ? window.location.pathname.replace(/\//g, '')
                : ''
          }
        ]
      }
    }
  });
};

export const pushProductViewed = (product: Product, cd_emarsys?: string) => {
  const { index } = findProductIndexInStorage({
    product,
    productBaseCode: product?.base_code
  });

  const ecommerceData = prepareProductData({
    product,
    index,
    emarsysCategory: cd_emarsys
  });

  const isGift = getIsGift(product);
  window?.dataLayer.push({
    ecommerce: {
      currency: product?.currency_type?.toUpperCase() || DEFAULT_CURRENCY,
      value: parseFloat(product?.price) || parseFloat(product?.retail_price),
      items: [
        {
          ...ecommerceData,
          is_gift: isGift
        }
      ]
    },
    event: 'view_item',
    search_type: getSearchType()
  });

  pushEvent({
    Action: 'Browse',
    Label: 'Product Detail Viewed',
    event: 'eeEvent',
    noninteraction: true,
    ecommerce: {
      detail: {
        actionField: { action: 'detail' },
        products: [
          {
            ...enhancedProductData({
              product,
              index
            }),
            is_gift: isGift,
            sku: product?.sku
          }
        ]
      }
    }
  });
};

export const pushAddToFavorite = (product: Product) => {
  const productColor =
    product?.attributes?.integration_renk ||
    product?.attributes?.integration_renk_tonu_kod ||
    product?.attributes_kwargs?.integration_renk_tonu_kod?.value ||
    '';

  window?.dataLayer.push({
    event: 'addToFavorite',
    itemgroup_id: product.base_code,
    pk: product.pk,
    name: product?.name || '',
    option: `${product.base_code}${productColor ? `.${productColor}` : ''}`,
    price: product?.price,
    sku: product?.sku
  });
};

export const pushAddToWishlist = (product: Product) => {
  const { index, affiliate } = findProductIndexInStorage({
    product,
    productBaseCode: product?.base_code
  });

  const ecommerceData = prepareProductData({
    product,
    index,
    affiliate
  });

  window?.dataLayer.push({
    ecommerce: {
      currency: product?.currency_type?.toUpperCase() || DEFAULT_CURRENCY,
      value: parseFloat(product?.price) || parseFloat(product?.retail_price),
      items: [{
        ...ecommerceData,
        is_gift: getIsGift(product)
      }]
    },
    event: 'add_to_wishlist',
    search_type: getSearchType()
  });
};

export const pushUserData = (
  value: Pick<
    GTMUserType,
    | 'pk'
    | 'hashedEmail'
    | 'dateOfBirth'
    | 'dateJoined'
    | 'gender'
    | 'firstName'
    | 'lastName'
    | 'phone'
    | 'emailAllowed'
  >
) => {
  const date = new Date(value?.dateJoined);
  const formattedDate = `${date.getFullYear()}${String(
    date.getMonth() + 1
  ).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
  const userData = {
    id: value?.pk ? md5Hash(value?.pk?.toString() ?? '') : '',
    email: value?.hashedEmail || '',
    phone: value?.phone ? md5Hash(value?.phone) : '',
    firstName: value?.firstName ? md5Hash(value?.firstName) : '',
    lastName: value?.lastName ? md5Hash(value?.lastName) : '',
    dateOfBirth: value?.dateOfBirth ? md5Hash(value?.dateOfBirth) : '',
    gender: value?.gender ? md5Hash(value?.gender) : '',
    event: 'userInfo',
    permission: value?.emailAllowed ? 'yes' : 'no',
    signupDate: formattedDate || ''
  };

  window?.dataLayer.push({ user_data: userData });
};

export const pushPurchaseEvent = (order) => {
  const transactionId = order?.number?.toString();
  const currency = order?.currency?.value?.toUpperCase() || DEFAULT_CURRENCY;
  const totalAmount = Number(order?.amount);

  const products = order?.orderitem_set?.map((item) => ({
    ...item?.product,
    price: item?.price,
    retail_price: item?.retail_price,
    quantity: item?.quantity || 1
  }));

  const mergeProductsByPk = (products) => {
    return products.reduce((acc, item) => {
      const existing = acc.find((p) => p.item_sku === item.item_sku);

      if (existing) {
        existing.quantity = (existing.quantity || 0) + (item.quantity || 0);
      } else {
        acc.push({
          ...item
        });
      }

      return acc;
    }, []);
  };


  const items = products?.map((product, idx) => {
    const { affiliate } = findProductIndexInStorage({
      product,
      productBaseCode: product?.base_code
    });

    const ecommerceData = prepareProductData({
      product,
      index: idx + 1,
      affiliate,
      quantity: +product?.quantity || 1,
      forceInStock: true
    });

    return {
      ...ecommerceData,
      is_gift: getIsGift(product)
    };
  });

  const mergedData = mergeProductsByPk(items);

  pushEventGA4('purchase', {
    search_type: getSearchType(),
    ecommerce: {
      transaction_id: transactionId,
      currency,
      value: totalAmount,
      shipping: +order.shipping_amount || 0,
      tax: +order.orderitem_set[0].tax_rate || 0,
      payment_type: order?.payment_option?.name || '',
      items: mergedData
    }
  });
};

export const pushPromotionEvent = (
  ecommerce: GTMPromotionData,
  isSelect: boolean
) => {
  window.dataLayer.push({
    event: isSelect ? 'select_promotion' : 'view_promotion',
    ecommerce: {
      promotion_id: ecommerce.promotion_id,
      promotion_name: ecommerce.promotion_name,
      creative_slot: ecommerce.creative_slot,
      items: ecommerce.items
    }
  });

  pushEvent({
    Action: 'Browse',
    Label: !isSelect ? 'Promotion Impressions' : 'Promotion Click',
    event: 'eeEvent',
    noninteraction: false,
    ecommerce: {
      [!isSelect ? 'promoView' : 'promoClick']: {
        promotions: [
          {
            creative: ecommerce.creative,
            id: ecommerce.promotion_id,
            name: ecommerce.promotion_name,
            position: ecommerce.position || '1'
          }
        ]
      }
    }
  });
};

export const pushEvent = (event: GTMEvent) => {
  const { Category = 'Enhanced Ecommerce', Value = 0, ...rest } = event;

  window.dataLayer.push({ Category, Value, ...rest });
};

export const pushEventGA4 = (
  eventAction: string,
  eventParams: GA4EventParams
) => {
  window.dataLayer.push({
    event: eventAction,
    ...eventParams
  });
};

export function md5Hash(message: string) {
  if (message) {
    const hash = CryptoJS.MD5(message).toString();
    return hash;
  }
}
